import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { SurveyPage, SurveyQuestion } from '../../fetchSurvey/types';
import type { StatementRow } from '../../fetchSurvey/types/shared';
import type { McqQuestion } from '../../fetchSurvey/types/mcq';
import type { RankOrderQuestion } from '../../fetchSurvey/types/rankOrder';
import type { SurveyAnswers, AnswerValue, MatrixRowAnswers } from '../../submitSurvey/types';
import { buildLogicContext } from '../../surveyLogic/context/buildLogicContext';
import type { CustomFieldValueMap } from '../../surveyLogic/types/customFieldValues';
import { normalizeHeatmapClicks } from '../../surveyLogic/fields/heatmapRegionUtils';
import {
  getVisibleMcqOptions,
  getVisibleRankOrderOptions,
  getVisibleStatementRows,
  isQuestionAnswerable,
} from '../../surveyLogic/visibility';
import { isRankOrderQuestionAnswerComplete } from '../../submitSurvey/utils/resolveRankOrderSubmitValues';
import { validateMcqOtherAnswers } from '../../surveyLogic/fields/validateMcqOtherAnswers';

function isDisplayOnly(question: SurveyQuestion): boolean {
  return question.type === QUESTION_TYPE.TEXT_AND_MEDIA;
}

function isMatrixType(type: SurveyQuestion['type']): boolean {
  return (
    type === QUESTION_TYPE.CFM_MATRIX ||
    type === QUESTION_TYPE.CSAT_MATRIX ||
    type === QUESTION_TYPE.RATING_MATRIX ||
    type === QUESTION_TYPE.SLIDER_MATRIX
  );
}

function isMatrixAnswered(
  rows: StatementRow[],
  answerMap: MatrixRowAnswers
): boolean {
  return rows.every(row => {
    const v = answerMap[row.id];
    return v !== undefined && v !== '';
  });
}

function getMcqSelectedCount(question: McqQuestion, value: AnswerValue | undefined): number {
  if (question.selectionMode === 'multiple') {
    return Array.isArray(value) ? value.length : 0;
  }
  return value !== undefined && value !== null && value !== '' ? 1 : 0;
}

function hasMcqAnswerConditions(question: McqQuestion): boolean {
  return question.minSelections !== undefined || question.maxSelections !== undefined;
}

function isMcqCountWithinConditions(question: McqQuestion, count: number): boolean {
  if (question.minSelections !== undefined && count < question.minSelections) {
    return false;
  }
  if (question.maxSelections !== undefined && count > question.maxSelections) {
    return false;
  }
  return true;
}

export function isQuestionAnswered(
  question: SurveyQuestion,
  answers: SurveyAnswers,
  allQuestions?: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): boolean {
  if (isDisplayOnly(question)) return true;

  const value = answers[question.id];

  if (question.type === QUESTION_TYPE.MCQ) {
    const mcq = question as McqQuestion;

    if (allQuestions) {
      const context = buildLogicContext(answers, allQuestions, customFieldValues);
      if (!isQuestionAnswerable(mcq, context)) {
        return true;
      }
    }

    const count = getMcqSelectedCount(mcq, value);

    if (hasMcqAnswerConditions(mcq)) {
      return isMcqCountWithinConditions(mcq, count) && count > 0;
    }

    if (mcq.selectionMode === 'multiple') {
      return count > 0;
    }

    return value !== undefined && value !== null && value !== '';
  }

  if (question.type === QUESTION_TYPE.RANK_ORDER) {
    const rankOrderQuestion = question as RankOrderQuestion;

    if (allQuestions) {
      const context = buildLogicContext(answers, allQuestions, customFieldValues);
      if (!isQuestionAnswerable(rankOrderQuestion, context)) {
        return true;
      }

      const visibleOptionIds = getVisibleRankOrderOptions(rankOrderQuestion, context).map(
        option => option.id
      );
      return isRankOrderQuestionAnswerComplete(rankOrderQuestion, value, visibleOptionIds);
    }

    const visibleOptionIds = rankOrderQuestion.options.map(option => option.id);
    return isRankOrderQuestionAnswerComplete(rankOrderQuestion, value, visibleOptionIds);
  }

  if (isMatrixType(question.type)) {
    if (typeof value !== 'object' || Array.isArray(value)) return false;
    const matrixQuestion = question as SurveyQuestion & { statementRows: StatementRow[] };
    let rows = matrixQuestion.statementRows;

    if (allQuestions) {
      const context = buildLogicContext(answers, allQuestions, customFieldValues);
      rows = getVisibleStatementRows(matrixQuestion, context);
    }

    if (rows.length === 0) {
      return true;
    }

    return isMatrixAnswered(rows, value as MatrixRowAnswers);
  }

  if (question.type === QUESTION_TYPE.HEATMAP) {
    return normalizeHeatmapClicks(value).length > 0;
  }

  return value !== undefined && value !== '';
}

export function validateQuestions(
  questions: SurveyQuestion[],
  answers: SurveyAnswers,
  language?: string,
  allQuestions?: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): Record<string, string> {
  const errors: Record<string, string> = {};
  const defaultMsg = language?.startsWith('de')
    ? 'Beantworten Sie bitte diese Frage bevor Sie fortfahren.'
    : 'Please answer this question before you proceed.';

  questions.forEach(question => {
    if (isDisplayOnly(question)) return;

    if (question.type === QUESTION_TYPE.MCQ) {
      const mcq = question as McqQuestion;
      const context = allQuestions
        ? buildLogicContext(answers, allQuestions, customFieldValues)
        : undefined;

      if (context && !isQuestionAnswerable(mcq, context)) {
        return;
      }

      const visibleOptions = context ? getVisibleMcqOptions(mcq, context) : mcq.options;
      const visibleOptionIds = new Set(visibleOptions.map(option => option.id));
      const rawValue = answers[question.id];
      let count = getMcqSelectedCount(mcq, rawValue);

      if (context && mcq.selectionMode === 'multiple' && Array.isArray(rawValue)) {
        count = rawValue.filter(id => typeof id === 'string' && visibleOptionIds.has(id)).length;
      } else if (context && mcq.selectionMode === 'single' && typeof rawValue === 'string') {
        count = visibleOptionIds.has(rawValue) ? 1 : 0;
      }

      const conditionError =
        mcq.answerConditionErrorMessage ?? mcq.requiredErrorMessage ?? defaultMsg;

      if (hasMcqAnswerConditions(mcq) && !isMcqCountWithinConditions(mcq, count)) {
        errors[question.id] = conditionError;
        return;
      }

      if (question.required && visibleOptions.length > 0 && count === 0) {
        errors[question.id] = question.requiredErrorMessage ?? defaultMsg;
        return;
      }

      const otherError = validateMcqOtherAnswers(mcq, answers, defaultMsg);
      if (otherError) {
        errors[question.id] = otherError;
      }

      return;
    }

    if (question.type === QUESTION_TYPE.RANK_ORDER) {
      const rankOrderQuestion = question as RankOrderQuestion;
      const context = allQuestions
        ? buildLogicContext(answers, allQuestions, customFieldValues)
        : undefined;

      if (context && !isQuestionAnswerable(rankOrderQuestion, context)) {
        return;
      }

      const visibleOptions = context
        ? getVisibleRankOrderOptions(rankOrderQuestion, context)
        : rankOrderQuestion.options;
      const visibleOptionIds = visibleOptions.map(option => option.id);

      if (
        question.required &&
        visibleOptionIds.length > 0 &&
        !isRankOrderQuestionAnswerComplete(rankOrderQuestion, answers[question.id], visibleOptionIds)
      ) {
        errors[question.id] = question.requiredErrorMessage ?? defaultMsg;
      }

      return;
    }

    if (question.required && !isQuestionAnswered(question, answers, allQuestions, customFieldValues)) {
      errors[question.id] = question.requiredErrorMessage ?? defaultMsg;
    }
  });

  return errors;
}

export function validateAllPages(
  pages: SurveyPage[],
  answers: SurveyAnswers,
  language?: string,
  allQuestions?: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): { errors: Record<string, string>; errorPageIndex: number; firstErrorId: string } | null {
  let errors: Record<string, string> = {};
  let firstErrorId = '';
  let errorPageIndex = -1;
  const questions = allQuestions ?? pages.flatMap(page => page.questions);

  for (let i = 0; i < pages.length; i++) {
    const pageErrors = validateQuestions(pages[i].questions, answers, language, questions, customFieldValues);
    if (Object.keys(pageErrors).length > 0) {
      errors = { ...errors, ...pageErrors };
      if (errorPageIndex === -1) {
        errorPageIndex = i;
        firstErrorId = Object.keys(pageErrors)[0];
      }
    }
  }

  if (errorPageIndex !== -1) {
    return { errors, errorPageIndex, firstErrorId };
  }

  return null;
}
